import { useState, useEffect } from "react";
import { BrowserRouter as Router } from "react-router-dom";
import "./firebase";
import { getAuth, onAuthStateChanged, signOut } from "firebase/auth";
import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import { ThemeProvider } from "./ThemeContext";
import LoginForm from "./LoginForm";
import AppRoutes from "./AppRoutes";
import SpotlightSearch from "./SpotlightSearch";
import "./App.css";

export default function App() {
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [roleLoading, setRoleLoading] = useState(false);
  const [showEmail, setShowEmail] = useState(false);

  const auth = getAuth();
  const db = getFirestore();

  useEffect(() => {
    // Defensive Firestore doc creation in onAuthStateChanged
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      setLoading(false);

      if (firebaseUser) {
        const userDocRef = doc(db, "users", firebaseUser.uid);
        const userDoc = await getDoc(userDocRef);
        if (!userDoc.exists()) {
          // Create the doc if it doesn't exist
          await setDoc(userDocRef, {
            email: firebaseUser.email,
            role: "new",
            createdAt: serverTimestamp(),
          });
        }
      }
    });
    return () => unsubscribe();
  }, [auth, db]);

  useEffect(() => {
    // Fetch user role from Firestore when user logs in
    const fetchUserRole = async () => {
      if (user) {
        setRoleLoading(true);
        try {
          const userDoc = await getDoc(doc(db, "users", user.uid));
          if (userDoc.exists()) {
            console.log("User role data:", userDoc.data());
            setUserRole(userDoc.data().role);
          } else {
            console.log("No user role document found");
            setUserRole(null); // Explicitly set to null if doc does not exist
          }
        } catch (err) {
          console.error("Error fetching user role:", err);
          setUserRole(null);
        }
        setRoleLoading(false);
      } else {
        setUserRole(null);
      }
    };
    fetchUserRole();
  }, [user, db]);

  if (loading || roleLoading) return <p>Loading...</p>;

  // User not logged in → show Login or Signup form
  if (!user) {
    return (
      <ThemeProvider>
        <LoginForm />
      </ThemeProvider>
    );
  }

  // Normalize role for all checks
  const normalizedRole = (userRole || "").toLowerCase().trim();

  // User is logged in but has "new" role → show waiting message
  if (normalizedRole === "new") {
    return (
      <ThemeProvider>
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3
              className="text-2xl font-bold cursor-pointer hover:text-blue-600 transition-colors"
              onClick={() => setShowEmail(!showEmail)}
              title="Click to toggle between name and email"
            >
              Welcome{" "}
              {showEmail
                ? user.email
                : user.displayName || user.email || "User"}
            </h3>
            <button
              onClick={() => signOut(auth)}
              className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600 logout_btn"
            >
              Logout
            </button>
          </div>
          <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 rounded">
            <p>
              Your account is awaiting approval. Please wait for an admin to
              grant you access.
            </p>
          </div>
        </div>
      </ThemeProvider>
    );
  }

  // Admins use the normal dashboard UI, with an Admin Dashboard button on Home
  if (normalizedRole === "admin") {
    let firstName = "";
    if (user.displayName) {
      firstName = user.displayName.split(" ")[0];
    } else if (user.email) {
      firstName = user.email.split("@")[0];
    }

    return (
      <ThemeProvider>
        <Router>
          <UserDashboardWrapper
            user={user}
            firstName={firstName}
            showEmail={showEmail}
            setShowEmail={setShowEmail}
            isAdmin
          />
        </Router>
      </ThemeProvider>
    );
  }

  // Only show dashboard if userRole is exactly "user"
  if (normalizedRole === "user") {
    // Extract first name from displayName or email
    let firstName = "";
    if (user.displayName) {
      firstName = user.displayName.split(" ")[0];
    } else if (user.email) {
      firstName = user.email.split("@")[0];
    }

    return (
      <ThemeProvider>
        <Router>
          <UserDashboardWrapper
            user={user}
            firstName={firstName}
            showEmail={showEmail}
            setShowEmail={setShowEmail}
          />
        </Router>
      </ThemeProvider>
    );
  }

  // Fallback for any other role
  return (
    <ThemeProvider>
      <div className="p-6">
        <h3 className="text-2xl font-bold mb-4">
          Access Restricted {userRole}
        </h3>
        <p>
          If you just signed up, please wait for an admin to approve your
          account.
        </p>
        <button
          onClick={() => signOut(auth)}
          className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600 logout_btn mt-4"
        >
          Logout
        </button>
      </div>
    </ThemeProvider>
  );
}

// Separate component to use useTheme hook
function UserDashboardWrapper({
  user,
  firstName,
  showEmail,
  setShowEmail,
  isAdmin = false,
}) {
  // const { isDarkMode, toggleTheme } = useTheme(); // Removed unused variables
  const [showSpotlight, setShowSpotlight] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Handle global keyboard shortcut (Cmd+K or Ctrl+K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setShowSpotlight(true);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <div className="min-h-screen">
      <div className={`appCont ${sidebarCollapsed ? "sidebar-collapsed" : ""}`}>
        {/* Desktop header content moved to sidebar */}
      </div>
      <AppRoutes
        user={user}
        firstName={firstName}
        showEmail={showEmail}
        setShowEmail={setShowEmail}
        sidebarCollapsed={sidebarCollapsed}
        setSidebarCollapsed={setSidebarCollapsed}
        setShowSpotlight={setShowSpotlight}
        isAdmin={isAdmin}
      />

      {/* Spotlight Search Modal */}
      <SpotlightSearch
        isOpen={showSpotlight}
        onClose={() => setShowSpotlight(false)}
      />
    </div>
  );
}
